/**
 * The water panel's arithmetic and vocabulary, kept out of the components.
 *
 * Nothing here renders, dispatches or reads React state — which is what makes
 * the panel's genuinely tricky parts (the alkalinity entry-form conversion, the
 * pH band, the acid-malt solve) unit-testable without a DOM.
 *
 * **No brewing maths is reimplemented here.** Ion accounting, the salt solver,
 * mash pH and every acid's equivalents all come from `@beerwright/core`; this
 * module only assembles core's inputs and translates its outputs into the
 * shapes the panel renders. If a number on screen ever disagrees with the
 * engine, it is because the engine changed, not because a second copy of the
 * formula drifted.
 */

import type {
  AcidChoiceId,
  RecipeAcidAddition,
  RecipeDoc,
  RecipeFermentable,
  RecipeSaltAddition,
  RecipeWater,
  SavedWaterProfileDoc,
  UnitPreferences,
  WaterProfileDoc,
  WaterRuleDoc,
} from '@beerwright/commands';
import {
  toAcidAddition,
  toFermentableAddition,
  toWaterProfile,
  waterRuleDocSchema,
} from '@beerwright/commands';
import type {
  AcidId,
  MashPhAgent,
  RecipeCalculation,
  SaltId,
  SulfateChlorideBalance,
  WaterIon,
  WaterProfile,
  WaterSolverResult,
  WaterTreatmentTarget,
} from '@beerwright/core';
import {
  ACID_DEFINITIONS,
  acidifyWater,
  alkalinityFromBicarbonate,
  bicarbonateFromAlkalinity,
  ION_LABELS,
  MASH_PH_BAND,
  MAX_SULFATE_CHLORIDE_RATIO,
  MIN_SULFATE_CHLORIDE_RATIO,
  predictMashPh,
  SULFATE_CHLORIDE_BAND_TARGETS,
  SULFATE_CHLORIDE_LABELS,
  solveAdditionsForTargetPh,
  solveWaterProfile,
  solveWaterRule,
  sulfateChlorideBalance,
  sulfateChlorideRatio,
  WATER_IONS,
  waterProfile,
} from '@beerwright/core';
import { formatNumber } from '@/lib/format';
import type { ParseResult } from '@/lib/units';
import { formatQuantityWithUnit } from '@/lib/units';

// ---------------------------------------------------------------------------
// Ion display
// ---------------------------------------------------------------------------

/**
 * The order the ion row reads in, which is **not** core's `WATER_IONS` order.
 *
 * Cations first, then anions, then alkalinity last — the order a water report
 * is printed in and the order a brewer scans for. Bicarbonate goes last because
 * it is the one that decides whether the mash needs acid, and the eye should
 * land on it after the flavour ions rather than in the middle of them.
 */
export const ION_DISPLAY_ORDER: readonly WaterIon[] = [
  'calcium',
  'magnesium',
  'sodium',
  'chloride',
  'sulfate',
  'bicarbonate',
];

/** Short chemical symbols, for the dense ion row. */
export const ION_SYMBOLS: Readonly<Record<WaterIon, string>> = {
  calcium: 'Ca',
  magnesium: 'Mg',
  sodium: 'Na',
  chloride: 'Cl',
  sulfate: 'SO₄',
  bicarbonate: 'HCO₃',
};

/**
 * Full English names, for form labels and screen readers.
 *
 * Not core's `ION_LABELS`, which are the charged chemical symbols (`Ca²⁺`) —
 * correct in a formula and unreadable to a screen reader, which pronounces the
 * superscripts. The visible label stays the symbol; this is what the assistive
 * text says.
 */
export const ION_NAMES: Readonly<Record<WaterIon, string>> = {
  calcium: 'Calcium',
  magnesium: 'Magnesium',
  sodium: 'Sodium',
  chloride: 'Chloride',
  sulfate: 'Sulfate',
  bicarbonate: 'Bicarbonate',
};

/** Core's charged symbols (`Ca²⁺`), for formulae and explanations. */
export const ION_CHARGED_SYMBOLS = ION_LABELS;

/**
 * Which ions a report is allowed to leave out.
 *
 * The document's own split, restated in core's ion vocabulary: sodium,
 * chloride and sulfate decide flavour and are routinely missing from a
 * municipal report; calcium, magnesium and bicarbonate carry the mash-pH
 * prediction and a profile without one of them is unusable rather than partial.
 */
export const ION_IS_OPTIONAL: Readonly<Record<WaterIon, boolean>> = {
  calcium: false,
  magnesium: false,
  sodium: true,
  chloride: true,
  sulfate: true,
  bicarbonate: false,
};

/**
 * The ppm of one ion, or `null` where the report never printed it.
 *
 * Returns the nullable reading rather than a display-ready number on purpose:
 * every caller is a place that shows a figure to a brewer, and each one has to
 * decide for itself what "we were never told" looks like. Anything that needs a
 * number for arithmetic should go through the bridge's `toWaterProfile`, which
 * is where a null becomes the engine's zero-plus-provenance.
 */
export function ionOf(profile: WaterProfileDoc | WaterProfile, ion: WaterIon): number | null {
  switch (ion) {
    case 'calcium':
      return profile.calciumPpm;
    case 'magnesium':
      return profile.magnesiumPpm;
    case 'sodium':
      return profile.sodiumPpm;
    case 'chloride':
      return profile.chloridePpm;
    case 'sulfate':
      return profile.sulfatePpm;
    case 'bicarbonate':
      return profile.bicarbonatePpm;
  }
}

/**
 * One ion for display: the rounded ppm, or `—` where there is no reading.
 *
 * The em dash is the same one the measured-pH field has always shown for "not
 * given", so the two gaps read as the same kind of gap.
 */
export function formatIonPpm(value: number | null, decimals = 0): string {
  return value === null ? '—' : formatNumber(value, decimals);
}

// ---------------------------------------------------------------------------
// Field parsing
// ---------------------------------------------------------------------------

/**
 * What the water fields accept, in one place.
 *
 * Both surfaces that edit water — the recipe panel's live fields and the Water
 * page's entry form — have to agree on what "45", "45.", "" and "forty-five"
 * mean, because the same brewer meets both. The panel feeds these to
 * {@link NumberField} and reverts on a rejection; the form holds the text until
 * submit and shows the `error` verbatim. Sharing the rule is what keeps a value
 * the panel accepts from being refused by the page.
 *
 * `Number` is used rather than `parseQuantity` because none of these carry a
 * unit: ppm is ppm in Illinois and in Bavaria, and a pH has no unit at all.
 */
function parsePlainNumber(text: string): ParseResult {
  const trimmed = text.trim();
  // Deliberately not `Number('')`, which is 0 — an empty field is not a zero.
  if (trimmed === '') return { ok: false, error: 'Enter a number.' };
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return { ok: false, error: `"${trimmed}" is not a number.` };
  return { ok: true, value };
}

/** An ion concentration or an alkalinity figure: a non-negative plain number. */
export function parsePpm(text: string): ParseResult {
  const parsed = parsePlainNumber(text);
  if (!parsed.ok) return parsed;
  if (parsed.value < 0) return { ok: false, error: 'A concentration cannot be negative.' };
  return parsed;
}

/** A parsed optional ion: a number, or `null` for a field left deliberately blank. */
export type OptionalPpmResult =
  | { readonly ok: true; readonly value: number | null }
  | { readonly ok: false; readonly error: string };

/**
 * An ion a report is allowed to omit: blank means **not measured**.
 *
 * The whole point of the nullable flavour ions. `parsePpm` rejects an empty
 * field with "Enter a number", which is the right answer for calcium — the
 * mash-pH prediction needs it — and exactly the wrong one for sulfate, where
 * the only way past the demand was to invent a zero and then be told what the
 * water tasted like. Anything actually typed is still held to the same rules.
 */
export function parseOptionalPpm(text: string): OptionalPpmResult {
  if (text.trim() === '') return { ok: true, value: null };
  return parsePpm(text);
}

/**
 * A water rule's calcium floor, ppm, held to the document schema's own range.
 *
 * The bound is read off `waterRuleDocSchema` rather than restated as a literal.
 * A field that accepted a number the schema then rejected would dispatch a
 * command the reducer refuses, and the brewer would watch their typing vanish
 * with no explanation — so the field asks the schema what it will hold.
 */
export function parseCalciumFloor(text: string): ParseResult {
  const parsed = parsePlainNumber(text);
  if (!parsed.ok) return parsed;
  if (!waterRuleDocSchema.shape.minCalciumPpm.safeParse(parsed.value).success) {
    return { ok: false, error: 'Enter a calcium minimum between 0 and 500 ppm.' };
  }
  return parsed;
}

/**
 * A sulfate-to-chloride balance, inside the range core's solver will fit.
 *
 * Core's bounds, not this field's: past either of them one ion dominates and the
 * number no longer describes a balance between two, which `solveWaterRule`
 * refuses outright. Catching it here means the refusal
 * arrives as the field reverting under the caret rather than as a throw.
 */
export function parseSulfateChlorideRatio(text: string): ParseResult {
  const parsed = parsePlainNumber(text);
  if (!parsed.ok) return parsed;
  if (parsed.value < MIN_SULFATE_CHLORIDE_RATIO || parsed.value > MAX_SULFATE_CHLORIDE_RATIO) {
    return {
      ok: false,
      error: `Enter a balance between ${MIN_SULFATE_CHLORIDE_RATIO} and ${MAX_SULFATE_CHLORIDE_RATIO}.`,
    };
  }
  return parsed;
}

/** A salt dose: a non-negative plain number of grams. Zero means "none". */
export function parseGrams(text: string): ParseResult {
  const parsed = parsePlainNumber(text);
  if (!parsed.ok) return parsed;
  if (parsed.value < 0) return { ok: false, error: 'A salt dose cannot be negative.' };
  return parsed;
}

/**
 * A pH, inside the range the field in question is willing to hold.
 *
 * The ranges differ and both are deliberate: a *target* mash pH outside 4–7 is
 * a typo rather than an ambition, while a *measured* water report can honestly
 * read anywhere from 3 to 11.
 */
export function parsePh(
  text: string,
  range: { readonly min: number; readonly max: number },
): ParseResult {
  const parsed = parsePlainNumber(text);
  if (!parsed.ok) return parsed;
  if (parsed.value < range.min || parsed.value > range.max) {
    return { ok: false, error: `Enter a pH between ${range.min} and ${range.max}.` };
  }
  return parsed;
}

/** The range a recipe's *target* mash pH is allowed to sit in. */
export const TARGET_PH_RANGE = { min: 4, max: 7 } as const;

/** The range a *measured* water-report pH is allowed to sit in. */
export const MEASURED_PH_RANGE = { min: 3, max: 11 } as const;

/** The document key one ion is stored under. */
export const ION_FIELD: Readonly<Record<WaterIon, keyof WaterProfileDoc & `${string}Ppm`>> = {
  calcium: 'calciumPpm',
  magnesium: 'magnesiumPpm',
  sodium: 'sodiumPpm',
  chloride: 'chloridePpm',
  sulfate: 'sulfatePpm',
  bicarbonate: 'bicarbonatePpm',
};

/**
 * `Ca 45 · Mg 8 · Na — · Cl 30 · SO₄ 40 · HCO₃ 90`, for the collapsed summary.
 *
 * An unmeasured ion reads `—`, which is the same em dash the pH field has
 * always used for "no reading" and is not a number anyone can mistake for one.
 */
export function ionSummary(profile: WaterProfileDoc | WaterProfile): string {
  return ION_DISPLAY_ORDER.map(
    (ion) => `${ION_SYMBOLS[ion]} ${formatIonPpm(ionOf(profile, ion))}`,
  ).join(' · ');
}

// ---------------------------------------------------------------------------
// The alkalinity entry form
// ---------------------------------------------------------------------------

/**
 * How a brewer's water report prints alkalinity.
 *
 * Storage is always bicarbonate; this decides only which number the form shows
 * and what a typed value is read as. Mirrors the persisted
 * `SavedWaterProfileDoc.alkalinityEntryForm`.
 */
export type AlkalinityForm = SavedWaterProfileDoc['alkalinityEntryForm'];

/** What the alkalinity field should display, given the stored bicarbonate. */
export function alkalinityDisplayValue(bicarbonatePpm: number, form: AlkalinityForm): number {
  return form === 'caco3' ? alkalinityFromBicarbonate(bicarbonatePpm) : bicarbonatePpm;
}

/** The bicarbonate to store, given what the brewer typed into the alkalinity field. */
export function alkalinityToBicarbonate(value: number, form: AlkalinityForm): number {
  return form === 'caco3' ? bicarbonateFromAlkalinity(value) : value;
}

/** The unit label the alkalinity field wears. */
export function alkalinityUnitLabel(form: AlkalinityForm): string {
  return form === 'caco3' ? 'ppm as CaCO₃' : 'ppm HCO₃⁻';
}

// ---------------------------------------------------------------------------
// Sulfate : chloride
// ---------------------------------------------------------------------------

/** A sulfate:chloride readout, ready to render. */
export interface SulfateChlorideReadout {
  /** The ratio, or `null` when there is none to print. */
  readonly ratio: number | null;
  /** The band label — a flavour claim, or the reason there isn't one. */
  readonly label: string;
  readonly band: ReturnType<typeof sulfateChlorideBalance>;
  /** Whether {@link label} is a claim about flavour rather than about the report. */
  readonly isFlavourClaim: boolean;
}

/**
 * The SO₄:Cl ratio with its descriptive band, straight from core.
 *
 * Takes the profile rather than a pre-divided ratio. That is the fix for the
 * bug this replaced: a profile with sulfate and no chloride divided to
 * `Infinity`, which is not finite, so the number was hidden — and then the band
 * computed from it said "Very hoppy — can turn harsh" beside the blank. The
 * quotient is not enough to know whether a band is warranted; the ppm and the
 * provenance are, and only the profile still has them.
 */
export function sulfateChlorideReadout(profile: WaterProfile): SulfateChlorideReadout {
  const band = sulfateChlorideBalance(profile);
  const ratio = sulfateChlorideRatio(profile);
  return {
    ratio: Number.isFinite(ratio) ? ratio : null,
    band,
    label: SULFATE_CHLORIDE_LABELS[band],
    isFlavourClaim: band !== 'unmeasured' && band !== 'indeterminate',
  };
}

// ---------------------------------------------------------------------------
// The water rule
// ---------------------------------------------------------------------------

/**
 * A band as a *sentence* says it: `hoppy`, not `Hoppy / bitter`.
 *
 * {@link SULFATE_CHLORIDE_LABELS} are column headings — they carry the gloss
 * that makes a band legible on its own in a table cell ("Very hoppy — can turn
 * harsh"), and dropped mid-sentence they read as a second clause pushed into
 * the first: "Reads as Very hoppy — can turn harsh." The rule editor says the
 * band inside a sentence, so it wants the word and not the gloss.
 *
 * Derived from core's labels rather than restated beside them. The gloss is
 * always introduced by a slash or an em dash, so trimming at the first of
 * either leaves exactly the word — and a band core renames is renamed here too,
 * which a second table of five strings could not promise.
 */
export function sulfateChlorideBandWord(band: SulfateChlorideBalance): string {
  const label = SULFATE_CHLORIDE_LABELS[band];
  return (label.split(/\s[—/]\s/)[0] ?? label).toLowerCase();
}

/** A band offered by name, and the ratio picking that name commits. */
export interface SulfateChlorideBandChoice {
  readonly band: keyof typeof SULFATE_CHLORIDE_BAND_TARGETS;
  /** The word as a button wears it: `Malty`, `Very hoppy`. */
  readonly label: string;
  /** The band's midpoint — what a brewer who said the word has said. */
  readonly ratio: number;
}

/**
 * The four bands a brewer can pick by name, malty to hoppy.
 *
 * `too-malty` is not among them on purpose: it is a verdict on a water, not an
 * ambition, and no brewer sets out to make one. It still appears in the live
 * readout when the number lands there, which is where a warning belongs.
 */
export const SULFATE_CHLORIDE_BAND_CHOICES: readonly SulfateChlorideBandChoice[] = (
  ['malty', 'balanced', 'hoppy', 'very-hoppy'] as const
).map((band) => {
  const word = sulfateChlorideBandWord(band);
  return {
    band,
    label: `${word.charAt(0).toUpperCase()}${word.slice(1)}`,
    ratio: SULFATE_CHLORIDE_BAND_TARGETS[band],
  };
});

/**
 * The band a bare ratio falls in, for a number that is being typed.
 *
 * Core bands a *profile*, not a quotient, and rightly so — the ppm and the
 * provenance are what decide whether a ratio is a flavour claim at all. A
 * number in a field has neither, so this hands core a water that carries the
 * ratio with plenty of both ions, which is the reading of "2.6" the brewer
 * means: not "my water is 2.6" but "aim for 2.6". Going through
 * {@link sulfateChlorideBalance} rather than re-testing the cut points is what
 * keeps the word under the field and the word in the readout the same word.
 */
export function balanceForRatio(ratio: number): SulfateChlorideBalance {
  return sulfateChlorideBalance(waterProfile({ chloridePpm: 100, sulfatePpm: 100 * ratio }));
}

/**
 * The rule, read back off the water that was actually made.
 *
 * The line a profile target gets is its six numbers repeated; the line a rule
 * gets has to be different, because repeating a rule tells the brewer nothing
 * they did not just type. So this reports the **achieved** water against the
 * two conditions — the calcium it reached beside the floor it had to clear, the
 * balance it landed on beside the one aimed at — which is the only version of
 * the sentence that can be wrong, and therefore the only one worth printing.
 *
 * The band is core's verdict on the treated water, not on the aim. When core
 * calls it `indeterminate` — sulfate and chloride together under
 * {@link SULFATE_CHLORIDE_MEANINGFUL_PPM}, or a ratio that is not a number at
 * all — the sentence says so instead of naming a flavour, because there is no
 * flavour there to name.
 *
 * @param achieved - `calculation.water.totalProfile`: the water in the kettle.
 * @param rule - The two conditions, as the document holds them.
 */
export function waterRuleReadout(achieved: WaterProfile, rule: WaterRuleDoc): string {
  const band = sulfateChlorideBalance(achieved);
  const verdict =
    band === 'indeterminate' ? 'too little of either to judge yet' : sulfateChlorideBandWord(band);
  return (
    `Calcium ${formatNumber(achieved.calciumPpm, 0)} ppm, ` +
    `floor ${formatNumber(rule.minCalciumPpm, 0)} · ` +
    `SO₄:Cl ${formatNumber(sulfateChlorideRatio(achieved), 1)}, ` +
    `aim ${formatNumber(rule.sulfateChlorideRatio, 1)} — ${verdict}.`
  );
}

// ---------------------------------------------------------------------------
// Mash pH
// ---------------------------------------------------------------------------

/**
 * The pH band the editor treats as "in range".
 *
 * 5.2–5.6 measured at room temperature is the range every modern reference
 * lands on: below it the beer reads thin and sharp and the enzymes slow, above
 * it tannin extraction and a dull, harsh bitterness set in. The **target**
 * inside that band defaults to 5.40 and is a per-recipe document field — the
 * band is where the number is acceptable, the target is where the brewer is
 * aiming.
 *
 * The numbers themselves live in `core/recipe-checks`, because the pre-brew
 * checks read the same band this badge does and the two must not be able to
 * drift apart. Re-exported here so the water panel's imports read the way they
 * always did.
 */
export { MASH_PH_BAND };

/** Whether a predicted mash pH is in band, and which way it missed. */
export type MashPhStatus = 'unknown' | 'low' | 'in-band' | 'high';

/** Classify a predicted mash pH against {@link MASH_PH_BAND}. */
export function mashPhStatus(ph: number): MashPhStatus {
  if (!Number.isFinite(ph)) return 'unknown';
  if (ph < MASH_PH_BAND.min) return 'low';
  if (ph > MASH_PH_BAND.max) return 'high';
  return 'in-band';
}

/** One line of English for each status, used on the badge and in its tooltip. */
export const MASH_PH_STATUS_LABELS: Readonly<Record<MashPhStatus, string>> = {
  unknown: "There's no mash pH to predict without grain in the mash",
  low: `Below the ${MASH_PH_BAND.min}–${MASH_PH_BAND.max} band — thin and sharp`,
  'in-band': `Inside the ${MASH_PH_BAND.min}–${MASH_PH_BAND.max} band`,
  high: `Above the ${MASH_PH_BAND.min}–${MASH_PH_BAND.max} band — risks tannin and harshness`,
};

// ---------------------------------------------------------------------------
// Acids
// ---------------------------------------------------------------------------

/** One choice in the acid picker: an acid at a strength, or acidulated malt. */
export interface AcidChoice {
  /**
   * Stable value for the `<select>`.
   *
   * Typed against commands' {@link AcidChoiceId} so the cupboard setting and
   * this list cannot drift: a strength added here without being added there
   * does not compile.
   */
  readonly value: AcidChoiceId | typeof ACID_MALT_CHOICE;
  readonly label: string;
  /** `null` for the acid-malt entry, which is a grist item rather than an acid. */
  readonly acid: AcidId | null;
  /** Strength, % w/w. `null` for a solid acid or for acid malt. */
  readonly concentrationPercent: number | null;
  /** True when the dose is weighed rather than measured by volume. */
  readonly solid: boolean;
}

/** The acid-malt option's `value`, and the catalog id it adds. */
export const ACID_MALT_CHOICE = 'acid-malt';

/** The acidulated malt the acid-malt path adds to the grist. */
export const ACID_MALT_CATALOG_ID = 'weyermann-acidulated';

/**
 * Every acid the picker offers, at the strengths brewers actually buy.
 *
 * The concentrations are core's own tabulated density points — the strengths
 * between them interpolate correctly, but these are the ones printed on a
 * bottle, so they are the ones offered. Acidulated malt is last because it is a
 * different kind of thing: it goes in the grist, not the water, and core reads
 * it off the fermentable list rather than the acid list.
 */
export const ACID_CHOICES: readonly AcidChoice[] = [
  {
    value: 'lactic-88',
    label: 'Lactic 88 %',
    acid: 'lactic',
    concentrationPercent: 88,
    solid: false,
  },
  {
    value: 'lactic-80',
    label: 'Lactic 80 %',
    acid: 'lactic',
    concentrationPercent: 80,
    solid: false,
  },
  {
    value: 'phosphoric-85',
    label: 'Phosphoric 85 %',
    acid: 'phosphoric',
    concentrationPercent: 85,
    solid: false,
  },
  {
    value: 'phosphoric-75',
    label: 'Phosphoric 75 %',
    acid: 'phosphoric',
    concentrationPercent: 75,
    solid: false,
  },
  {
    value: 'phosphoric-10',
    label: 'Phosphoric 10 %',
    acid: 'phosphoric',
    concentrationPercent: 10,
    solid: false,
  },
  {
    value: 'citric-solid',
    label: 'Citric (solid)',
    acid: 'citric',
    concentrationPercent: null,
    solid: true,
  },
  {
    value: 'acetic-5',
    label: 'Acetic 5 % (vinegar)',
    acid: 'acetic',
    concentrationPercent: 5,
    solid: false,
  },
  {
    value: 'hydrochloric-31',
    label: 'Hydrochloric 31 %',
    acid: 'hydrochloric',
    concentrationPercent: 31,
    solid: false,
  },
  {
    value: 'sulfuric-35',
    label: 'Sulfuric 35 %',
    acid: 'sulfuric',
    concentrationPercent: 35,
    solid: false,
  },
  {
    value: ACID_MALT_CHOICE,
    label: 'Acidulated malt (grist)',
    acid: null,
    concentrationPercent: null,
    solid: true,
  },
];

/** The default choice: 88 % lactic, what most homebrew shops sell. */
export const DEFAULT_ACID_CHOICE = ACID_CHOICES[0] as AcidChoice;

/** A picker entry that is a bottle a brewer can stock, rather than the grist item. */
export type StockableAcidChoice = AcidChoice & { readonly value: AcidChoiceId };

/** Acid malt is the one entry no cupboard holds. @see STOCKABLE_ACID_CHOICES */
function isStockable(choice: AcidChoice): choice is StockableAcidChoice {
  return choice.value !== ACID_MALT_CHOICE;
}

/** Every acid a cupboard can hold: the picker's list, minus acidulated malt. */
export const STOCKABLE_ACID_CHOICES: readonly StockableAcidChoice[] =
  ACID_CHOICES.filter(isStockable);

/** Look a picker choice up by its select value. */
export function acidChoice(value: string): AcidChoice {
  return ACID_CHOICES.find((choice) => choice.value === value) ?? DEFAULT_ACID_CHOICE;
}

/**
 * The picker list for a brewer's cupboard — `settings.stockedAcids`.
 *
 * Kept in {@link ACID_CHOICES}' order rather than the cupboard's, so the
 * dropdown reads the same way for everyone who stocks the same bottles.
 *
 * Two entries survive an unticked box. Acidulated malt is always here: it is a
 * grist item rather than a bottle, so the cupboard has no opinion on it. And an
 * acid a document already records stays offered even when it is not stocked —
 * an imported recipe, or a box unticked after the fact — because a `<select>`
 * whose value matches no option renders blank, and a brewer looking at a dose
 * they took should see what they took.
 *
 * @param stocked - The workspace's cupboard.
 * @param recorded - A choice the document already holds, if any.
 */
export function stockedAcidChoices(
  stocked: readonly AcidChoiceId[],
  recorded?: string | null,
): readonly AcidChoice[] {
  return ACID_CHOICES.filter(
    (choice) => !isStockable(choice) || choice.value === recorded || stocked.includes(choice.value),
  );
}

/**
 * The acid a picker opens on: the first one stocked, in the picker's order.
 *
 * This is what stops a brewer who only buys phosphoric re-picking it on every
 * recipe. The schema refuses an empty cupboard, so the fallback is only ever
 * reached by a caller holding a list from somewhere else.
 */
export function defaultAcidChoice(stocked: readonly AcidChoiceId[]): AcidChoice {
  return (
    STOCKABLE_ACID_CHOICES.find((choice) => stocked.includes(choice.value)) ?? DEFAULT_ACID_CHOICE
  );
}

/** The alkali offered when the mash is *below* target and needs raising. */
export const DEFAULT_ALKALI: SaltId = 'baking-soda';

// ---------------------------------------------------------------------------
// Salts
// ---------------------------------------------------------------------------

/** Every salt the manual table lists, in the order it lists them. */
export const SALT_ORDER: readonly SaltId[] = [
  'gypsum',
  'calcium-chloride-dihydrate',
  'calcium-chloride-anhydrous',
  'epsom-salt',
  'magnesium-chloride',
  'table-salt',
  'baking-soda',
  'chalk',
  'slaked-lime',
];

/** Human labels for the salt table. Formulae included: brewers read the bag. */
export const SALT_LABELS: Readonly<Record<SaltId, string>> = {
  gypsum: 'Gypsum (CaSO₄·2H₂O)',
  'calcium-chloride-dihydrate': 'Calcium chloride, dihydrate (CaCl₂·2H₂O)',
  'calcium-chloride-anhydrous': 'Calcium chloride, anhydrous (CaCl₂)',
  'epsom-salt': 'Epsom salt (MgSO₄·7H₂O)',
  'magnesium-chloride': 'Magnesium chloride (MgCl₂·6H₂O)',
  'table-salt': 'Table salt (NaCl)',
  'baking-soda': 'Baking soda (NaHCO₃)',
  chalk: 'Chalk (CaCO₃)',
  'slaked-lime': 'Slaked lime (Ca(OH)₂)',
};

/**
 * Dense names for the collapsed additions line.
 *
 * Formulae where they scan faster than the bag name (the two CaCl₂ hydrates,
 * Epsom vs MgCl₂); common names for the rest. The expanded table keeps the
 * long labels — this is only the one-line summary.
 */
export const SALT_SHORT_LABELS: Readonly<Record<SaltId, string>> = {
  gypsum: 'Gypsum',
  'calcium-chloride-dihydrate': 'CaCl₂',
  'calcium-chloride-anhydrous': 'CaCl₂ anhyd.',
  'epsom-salt': 'Epsom',
  'magnesium-chloride': 'MgCl₂',
  'table-salt': 'NaCl',
  'baking-soda': 'Baking soda',
  chalk: 'Chalk',
  'slaked-lime': 'Slaked lime',
};

/**
 * Salts named in a sentence: "Gypsum", "Gypsum and Epsom salt", "Gypsum, Epsom
 * salt and Table salt".
 *
 * {@link SALT_LABELS} with the formula dropped, which is what the suggested
 * schedule already prints and what the checkbox list next door reads without
 * its brackets. The short labels were tried first and were wrong here: a
 * sentence about a brewer's cupboard calling a bag "NaCl" is a register the
 * surfaces around it do not use, and the same salt was named two ways two lines
 * apart. Both surfaces that report which cupboard the solver is reading share
 * this, so they cannot disagree either (PLAN-UX-PASS C5).
 */
export function saltNameList(salts: readonly SaltId[]): string {
  const names = salts.map((salt) => SALT_LABELS[salt].replace(/\s*\(.*\)$/, ''));
  const last = names.at(-1);
  if (last === undefined) return '';
  if (names.length === 1) return last;
  return `${names.slice(0, -1).join(', ')} and ${last}`;
}

/** Where a treatment goes, for the per-row select. */
export const TREATMENT_TARGETS: readonly { value: WaterTreatmentTarget; label: string }[] = [
  { value: 'total', label: 'Total' },
  { value: 'mash', label: 'Mash' },
  { value: 'sparge', label: 'Sparge' },
];

// ---------------------------------------------------------------------------
// The solver
// ---------------------------------------------------------------------------

/** What {@link runSaltSolver} needs, gathered from the document and the calculation. */
export interface SolverContext {
  readonly source: WaterProfileDoc;
  /** Six numbers to be fitted, or `null` when the section aims elsewhere. */
  readonly target: WaterProfileDoc | null;
  /** Two conditions to be satisfied, or `null`. Never set alongside a target. */
  readonly rule: WaterRuleDoc | null;
  readonly dilutionFraction: number;
  readonly mashVolumeL: number;
  readonly spargeVolumeL: number;
  /**
   * The salts the caller allows: the inventory's usable salts when it holds
   * any, otherwise `settings.solverSalts`.
   */
  readonly allowedSalts: readonly SaltId[];
}

/**
 * Run core's salt solver for this recipe, against whichever target is set.
 *
 * Client-side and synchronous on purpose: a solve enumerates at most 130 salt
 * subsets and takes about 0.2 ms, so there is no loading state to design, no
 * request to cancel and no stale result to reconcile. The salts on offer are
 * the ones the caller allows: the inventory's usable salts when it holds any,
 * otherwise the workspace's `solverSalts` setting. The rest of the defaults are
 * core's — at most three salts, doses rounded to 0.1 g because that is what a
 * brewing scale reads.
 *
 * ## Two targets, one result type
 *
 * A rule goes to `solveWaterRule`, which returns a `WaterRuleSolverResult` —
 * a `WaterSolverResult` with a verdict bolted on. That inheritance is the whole
 * reason this branch is three lines rather than a second pipeline: the schedule
 * the panel stages, the per-ion table it previews and `solverAdditionsToDoc`
 * all read the base shape and cannot tell which solver ran.
 *
 * The rule branch takes precedence, but the two are never both set — the
 * document schema forbids it and both setters maintain it — so the order is a
 * formality rather than a policy.
 *
 * @returns The schedule, or `null` when the section names no target at all. The
 *   Suggest button is disabled in that state; answering `null` rather than
 *   throwing means a stray click stages nothing instead of crashing the panel,
 *   and refusing rather than defaulting means no salt is ever suggested towards
 *   a target the brewer did not set.
 */
export function runSaltSolver(context: SolverContext): WaterSolverResult | null {
  const volumes = { mashVolumeL: context.mashVolumeL, spargeVolumeL: context.spargeVolumeL };
  const source = toWaterProfile(context.source);
  if (context.rule !== null) {
    return solveWaterRule({
      source,
      rule: context.rule,
      dilutionFraction: context.dilutionFraction,
      volumes,
      allowedSalts: context.allowedSalts,
    });
  }
  if (context.target === null) return null;
  return solveWaterProfile({
    source,
    target: toWaterProfile(context.target),
    dilutionFraction: context.dilutionFraction,
    volumes,
    allowedSalts: context.allowedSalts,
  });
}

/**
 * The solver's schedule as recipe salt additions.
 *
 * Grams are rounded to two decimals on the way in. The solver already rounds to
 * the 0.1 g a brewing scale reads, but it does so in binary floating point, so
 * "3.3 g" arrives as `3.3000000000000003` — which a number input then renders
 * in full. Rounding here rather than at the input means the *stored* document
 * holds the number the brewer was shown, and the two cannot diverge.
 *
 * @param result - What {@link runSaltSolver} returned.
 * @param mintId - Supplies the per-row id; the reducer mints none (see the
 *   commands README), so the caller owns it.
 */
export function solverAdditionsToDoc(
  result: WaterSolverResult,
  mintId: () => string,
): RecipeSaltAddition[] {
  return result.additions.map((addition) => ({
    additionId: mintId(),
    salt: addition.salt,
    grams: round(addition.grams, 2),
    target: addition.target ?? 'total',
    dissolvedFraction: null,
  }));
}

/**
 * A gram value as a number input's `value`.
 *
 * Rounded to two decimals and stripped of a trailing `.0`, so a stored 3.3
 * reads as `3.3` and never as `3.3000000000000003`.
 */
export function gramsInputValue(grams: number): string {
  return String(round(grams, 2));
}

/**
 * An acid dose as a sentence reads it: `2.35 mL`, `1.4 g`.
 *
 * Through `@/lib/units` rather than by concatenating a literal, so the unit
 * label has one definition in the app. Both units are deliberately *not*
 * converted by the workspace's preference: a syringe is marked in millilitres
 * and a jeweller's scale in grams wherever the brewer lives, and an acid dose in
 * fluid ounces would be unmeasurable rather than familiar.
 */
export function formatAcidAmount(amount: number, unit: 'mL' | 'g', units: UnitPreferences): string {
  return unit === 'mL'
    ? formatQuantityWithUnit(amount / 1000, 'volume', units, { unit: 'ml', decimals: 2 })
    : formatQuantityWithUnit(amount / 1000, 'miscMass', units, { unit: 'g', decimals: 2 });
}

// ---------------------------------------------------------------------------
// pH corrections
// ---------------------------------------------------------------------------

/** Everything a pH solve needs about the recipe, gathered once. */
export interface MashPhContext {
  readonly grist: readonly RecipeFermentable[];
  readonly mashWater: WaterProfile;
  readonly mashVolumeL: number;
  readonly existingAcids: readonly RecipeAcidAddition[];
}

/** Assemble a {@link MashPhContext} from a document and its calculation. */
export function mashPhContext(
  doc: RecipeDoc,
  calculation: RecipeCalculation,
): MashPhContext | null {
  const water = calculation.water;
  if (water === undefined || doc.water === null) return null;
  return {
    grist: doc.fermentables,
    mashWater: water.mashProfile,
    mashVolumeL: water.mashVolumeL,
    // Only the mash share of a `total` acid affects mash pH, and core's
    // `calculateRecipe` already split it — but the solver takes the raw list
    // and splits it the same way, so the document's own additions go in whole.
    existingAcids: doc.water.acidAdditions,
  };
}

/** A dose the panel can offer to apply in one click. */
export interface DoseSuggestion {
  readonly amount: number;
  readonly unit: 'mL' | 'g';
  readonly achievedPh: number;
  readonly baselinePh: number;
  readonly unreachable: boolean;
  readonly warnings: readonly string[];
}

/**
 * How much of the chosen agent reaches the target mash pH.
 *
 * Delegates to core's bisection-on-the-dose solver, which re-runs the whole
 * charge balance at every step — so feeding the returned amount back into
 * `predictMashPh` really does produce the target, rather than a linearisation
 * of it.
 */
export function solveDose(
  context: MashPhContext,
  targetPh: number,
  agent: MashPhAgent,
): DoseSuggestion {
  const solution = solveAdditionsForTargetPh({
    targetPh,
    agent,
    grist: context.grist.map(toFermentableAddition),
    water: context.mashWater,
    mashVolumeL: context.mashVolumeL,
    existingAcidAdditions: context.existingAcids.map(toAcidAddition),
  });
  return {
    amount: solution.amount,
    unit: solution.unit,
    achievedPh: solution.achievedPh,
    baselinePh: solution.baselinePh,
    unreachable: solution.unreachable,
    warnings: solution.warnings,
  };
}

/** The mash-pH agent a picker choice maps onto, or `null` for the acid-malt path. */
export function agentFor(choice: AcidChoice): MashPhAgent | null {
  if (choice.acid === null) return null;
  return {
    kind: 'acid',
    acid: choice.acid,
    ...(choice.concentrationPercent === null
      ? {}
      : { concentrationPercent: choice.concentrationPercent }),
    ...(choice.solid ? { asSolid: true } : {}),
  };
}

/** A new acid addition row for a solved dose. */
export function doseToAcidAddition(
  choice: AcidChoice,
  dose: DoseSuggestion,
  additionId: string,
): RecipeAcidAddition | null {
  if (choice.acid === null) return null;
  return {
    additionId,
    acid: choice.acid,
    millilitres: dose.unit === 'mL' ? round(dose.amount, 2) : null,
    grams: dose.unit === 'g' ? round(dose.amount, 2) : null,
    concentrationPercent: choice.concentrationPercent,
    target: 'mash',
  };
}

// ---------------------------------------------------------------------------
// Acidulated malt
// ---------------------------------------------------------------------------

/** Upper bound on the acid-malt search: 20 % of the grist is already absurd. */
const MAX_ACID_MALT_FRACTION = 0.2;

/** Bisection steps. 40 halvings of a 20 %-of-grist bracket resolves to well under a gram. */
const ACID_MALT_ITERATIONS = 40;

/**
 * The acidulated-malt row already in a grist, if there is one.
 *
 * Matched on the catalog stamp first, because that is what a row added from the
 * catalog carries and it survives a rename — a brewer who calls their row
 * "Sauermalz" still gets the offer. The name is the fallback for a row typed by
 * hand or imported from another app, where there is no stamp to read.
 *
 * The *first* match wins and the rest are left alone. More than one is possible
 * today only because the panel used to append rather than adjust; picking one
 * and saying what it did beats silently merging rows a brewer separated on
 * purpose.
 *
 * @param grist - The recipe's fermentables.
 */
export function findAcidMaltRow(
  grist: readonly RecipeFermentable[],
): RecipeFermentable | undefined {
  return grist.find(
    (row) =>
      row.catalogRef?.id === ACID_MALT_CATALOG_ID ||
      /acidulated|sauermalz|acid malt/i.test(row.name),
  );
}

/**
 * Grams of acidulated malt that reach a target mash pH.
 *
 * Core has no acid-malt agent, and cannot sensibly have one: acidulated malt is
 * a *grist* item, so adding it changes the buffering capacity and the weighted
 * DI pH at the same time as it adds acid. Treating it as "lactic acid in a bag"
 * — the linear conversion every calculator does — overstates the dose, because
 * it ignores the base malt riding along with the acid.
 *
 * So this bisects on grams, re-running `predictMashPh` with the malt actually
 * *in* the grist at each step. Same shape as core's own dose solver, same
 * self-consistency guarantee: feed the answer back in and the target comes out.
 * Roughly 40 charge-balance solves, still well under a millisecond.
 *
 * @param context - The mash as it stands, without the acid malt.
 * @param targetPh - The pH to reach.
 * @param template - The acidulated-malt grist row, at any amount; its `amountKg`
 *   is replaced on every trial.
 * @returns Grams of acid malt, or `null` when the target is already met or is
 *   out of reach below {@link MAX_ACID_MALT_FRACTION} of the grist.
 */
export function solveAcidMaltGrams(
  context: MashPhContext,
  targetPh: number,
  template: RecipeFermentable,
): number | null {
  const gristKg = context.grist.reduce((total, item) => total + item.amountKg, 0);
  if (gristKg <= 0) return null;

  const phAt = (kg: number): number =>
    predictMashPh({
      grist: [...context.grist, { ...template, amountKg: kg }].map(toFermentableAddition),
      water: context.mashWater,
      mashVolumeL: context.mashVolumeL,
      acidAdditions: context.existingAcids.map(toAcidAddition),
    }).ph;

  const baseline = phAt(0);
  if (!Number.isFinite(baseline) || baseline <= targetPh) return null;

  let high = gristKg * MAX_ACID_MALT_FRACTION;
  if (phAt(high) > targetPh) return null;

  let low = 0;
  for (let i = 0; i < ACID_MALT_ITERATIONS; i += 1) {
    const mid = (low + high) / 2;
    if (phAt(mid) > targetPh) low = mid;
    else high = mid;
  }
  return round(high * 1000, 1);
}

// ---------------------------------------------------------------------------
// Sparge acidification
// ---------------------------------------------------------------------------

/** The sparge target pH range PLAN §5.4 gives; 5.5 is core's default. */
export const SPARGE_TARGET_PH = 5.5;

/** What the sparge water needs to reach {@link SPARGE_TARGET_PH}. */
export interface SpargeDose {
  readonly amount: number;
  readonly unit: 'mL' | 'g';
  readonly volumeL: number;
  readonly warnings: readonly string[];
}

/**
 * Acid to bring the sparge water to pH 5.5.
 *
 * Computed here rather than read off `calculation.water.spargeAcid` because the
 * engine only reports it when the recipe's `acidifySparge` flag is set, and
 * there is no command that sets that flag. The panel shows the dose as advice
 * regardless, and *applying* it appends an ordinary acid addition with
 * `target: 'sparge'` — a real, persisted, undoable, syncable row rather than a
 * boolean the brewer cannot see on brew day.
 *
 * @returns `null` when there is no sparge (BIAB and no-sparge systems) or the
 *   water needs nothing.
 */
export function spargeAcidDose(
  spargeProfile: WaterProfile,
  spargeVolumeL: number,
  choice: AcidChoice,
): SpargeDose | null {
  if (spargeVolumeL <= 0 || choice.acid === null) return null;
  const result = acidifyWater({
    profile: spargeProfile,
    volumeL: spargeVolumeL,
    targetPh: SPARGE_TARGET_PH,
    acid: choice.acid,
    ...(choice.concentrationPercent === null
      ? {}
      : { concentrationPercent: choice.concentrationPercent }),
  });
  const amount = choice.solid ? (result.grams ?? 0) : (result.millilitres ?? 0);
  if (amount <= 0) return null;
  return {
    amount: round(amount, 2),
    unit: choice.solid ? 'g' : 'mL',
    volumeL: spargeVolumeL,
    warnings: result.warnings,
  };
}

// ---------------------------------------------------------------------------
// Panel state
// ---------------------------------------------------------------------------

/** Which of the panel's three shapes to render. */
export type WaterPanelState = 'empty' | 'configured';

/** Whether the recipe carries a water section at all. */
export function waterPanelState(doc: RecipeDoc): WaterPanelState {
  return doc.water === null ? 'empty' : 'configured';
}

/** The dense one-line summary the collapsed panel shows. */
export interface WaterSummary {
  readonly sourceName: string;
  readonly ions: string;
  readonly ph: number;
  readonly status: MashPhStatus;
  readonly dilutionPercent: number;
  /** Salts and acids on the recipe, or `null` when nothing is added. */
  readonly treatments: string | null;
}

function formatSaltTreatment(addition: RecipeSaltAddition): string | null {
  if (addition.grams <= 0) return null;
  return `${round(addition.grams, 1)} g ${SALT_SHORT_LABELS[addition.salt]} → ${addition.target}`;
}

function formatAcidTreatment(addition: RecipeAcidAddition): string | null {
  const amount = addition.millilitres ?? addition.grams ?? 0;
  if (amount <= 0) return null;
  const unit = addition.millilitres === null ? 'g' : 'mL';
  const name = ACID_DEFINITIONS[addition.acid].label.replace(/ acid$/i, '');
  const strength =
    addition.concentrationPercent === null ? '' : ` ${Math.round(addition.concentrationPercent)} %`;
  return `${round(amount, 2)} ${unit} ${name}${strength} → ${addition.target}`;
}

/**
 * Salts and acids as one dense line, or `null` when nothing is added.
 *
 * The collapsed panel shows this so a brewer can see what they are putting in
 * the water without expanding — ions and pH are the outcome; this is the bill
 * of materials.
 */
export function treatmentSummary(water: RecipeWater): string | null {
  const parts = [
    ...water.saltAdditions.map(formatSaltTreatment),
    ...water.acidAdditions.map(formatAcidTreatment),
  ].filter((part): part is string => part !== null);
  return parts.length === 0 ? null : parts.join(' · ');
}

/** Build the collapsed summary, or `null` when there is no water section. */
export function waterSummary(
  water: RecipeWater | null,
  calculation: RecipeCalculation,
): WaterSummary | null {
  if (water === null || calculation.water === undefined) return null;
  const ph = calculation.water.mashPh.ph;
  return {
    // Same words the source picker uses for a report that is not in the
    // library, so the collapsed header and the picker cannot disagree.
    sourceName: water.sourceProfile.name ?? 'Unsaved report',
    ions: ionSummary(calculation.water.totalProfile),
    ph,
    status: mashPhStatus(ph),
    dilutionPercent: water.dilutionFraction * 100,
    treatments: treatmentSummary(water),
  };
}

/** Round half-up to `decimals` places. Local so this module imports no formatter. */
function round(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/** Re-exported so components need only one import for the ion vocabulary. */
export { ACID_DEFINITIONS, WATER_IONS };
